import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { AnimatePresence, easeOut, motion } from 'motion/react';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useParams } from 'react-router';
import { Avatar } from '@/components/Avatar';
import { Button, Text } from '@/components/base';
import Icon, { IconName } from '@/components/base/Icon';
import LanguageSwitchModal from '@/components/LanguageSwitchModal';
import { useAuthContext } from '@/contexts/authContext';
import { UserRole } from '@/model/User.model';
import { authenticatedRoutes } from '@/router/routes';
import { RoleBadge } from '@/routes/SchoolApp/components/RoleBadge';
import { SidebarMenu } from '@/routes/SchoolApp/components/SidebarMenu';
import { schoolRoutes } from '@/routes/SchoolApp/router/routes';
import { toClassNames } from '@/utils';

interface ResolverProps {
  isOpen: boolean;
  onClose: () => void;
}

interface SideBarProps extends ResolverProps {
  schoolId?: string;
  userId: string;
}

const SchoolSideBarParamsResolver = ({ isOpen, onClose }: ResolverProps) => {
  const { schoolId } = useParams();
  const { user } = useAuthContext();
  if (!user?.id) {
    return <Navigate to={schoolRoutes.SchoolHome.getRoute()} />;
  }
  return <SchoolSideBar schoolId={schoolId} isOpen={isOpen} onClose={onClose} userId={user.id} />;
};

const SchoolSideBar = ({ isOpen }: SideBarProps) => {
  const { user, logout } = useAuthContext();
  const { t } = useTranslation('schoolApp');
  const [languageSwitchModalOpen, setLanguageSwitchModalOpen] = useState(false);

  const lessonPageRoute =
    user?.classroomIds?.length === 1
      ? authenticatedRoutes.Lessons.getRoute({ classroomId: user.classroomIds[0] })
      : authenticatedRoutes.Classrooms.getRoute();

  return (
    <div
      className={toClassNames(
        'fixed flex min-h-screen w-64 -translate-x-full transform flex-col gap-y-3 border-r border-gray-200 bg-white px-3 pb-20 pt-8 text-center text-gray-500 transition-all duration-500',
        isOpen && 'translate-x-0'
      )}
    >
      <SidebarMenu />
      <div className="-mx-3 mt-auto border-b border-gray-300" />

      {/* Instructor Accordion Menu */}
      <Disclosure>
        {({ open }) => (
          <div className="flex flex-col-reverse">
            <DisclosureButton
              as="button"
              className={toClassNames('mt-2 flex w-full cursor-pointer items-center gap-2')}
            >
              <Avatar name={user?.firstname ?? ''} className="h-10 w-10" />
              <div className="space-y-1.5">
                <Text variant="sm" className="text-left font-medium text-primary-900">
                  {user?.firstname} {user?.lastname}
                </Text>
                {user?.role && <RoleBadge role={user.role} className="w-fit" />}
              </div>
              <span
                className={toClassNames(
                  'icon-[line-md--chevron-up] ml-auto transition-transform',
                  open ? 'rotate-180' : 'rotate-0'
                )}
              />
            </DisclosureButton>
            <AnimatePresence>
              {open && (
                <DisclosurePanel static as={Fragment}>
                  <motion.div
                    initial={{ opacity: 0, height: 0, marginBottom: 0 }}
                    animate={{ opacity: 1, height: 'auto', marginBottom: 12 }}
                    exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                    transition={{ duration: 0.25, ease: easeOut }}
                    className="origin-bottom overflow-hidden"
                  >
                    <div className="rounded-md border border-gray-100 bg-gray-light">
                      <Button
                        variant="unstyled"
                        className={toClassNames('flex w-full items-center gap-2 px-2 py-3 text-gray-700')}
                        onClick={() => {
                          setLanguageSwitchModalOpen(true);
                        }}
                      >
                        <span className="icon-[material-symbols--language] text-2xl" />
                        <Text variant="lg">Change Language</Text>
                      </Button>
                      {user?.role === UserRole.Instructor && (
                        <Link to={lessonPageRoute}>
                          <Button
                            variant="unstyled"
                            className={toClassNames('flex w-full items-center gap-2 px-2 py-3 text-gray-700')}
                          >
                            <Icon icon={IconName.UserSwitch} size="24px" />
                            <Text variant="lg" className="text-left">
                              {t('sidebar.switch_to_student')}
                            </Text>
                          </Button>
                        </Link>
                      )}
                      <Button
                        variant="unstyled"
                        className={toClassNames('flex w-full items-center gap-2 px-2 py-3 text-progress-red')}
                        onClick={() => logout?.()}
                      >
                        <Icon icon={IconName.Logout} size="24px" />
                        <Text variant="lg">{t('sidebar.logout')}</Text>
                      </Button>
                    </div>
                  </motion.div>
                </DisclosurePanel>
              )}
            </AnimatePresence>
          </div>
        )}
      </Disclosure>

      <LanguageSwitchModal open={languageSwitchModalOpen} onClose={() => setLanguageSwitchModalOpen(false)} />
    </div>
  );
};

export default SchoolSideBarParamsResolver;
